import {
  ImpactScoreResultImg,
  ThreeLevelScale,
} from "@/pages/common/report/WebReport2/commons";
import { ImpactScore } from "@/pages/common/report/WebReport2/index";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { MoveImprovement, SectionLayout } from ".";
import ComprehensiveEvaluationHelpDialog from "../commons/dialogs/ComprehensiveEvaluationHelpDialog";
import { ImprovementComparisonType } from "../commons/sleep_summaries";
import { SleepSummary2 } from "../commons/sleep_summaries_2";
import WorksheetDialog from "../commons/WorksheetDialog";

export interface SleepJudgementComparison {
  result: string;
  last_time_result: string | null;
  improvement: ImprovementComparisonType | null;
}

interface JudgementSectionProps {
  uuid: string;
  judgement: SleepSummary2["judgement"];
  quantityJudgement: SleepJudgementComparison;
  qualityJudgement: SleepJudgementComparison;
  rhythmJudgement: SleepJudgementComparison;
  impactScores: ImpactScore[];
  SASJudgement?: SleepJudgementComparison;
  insomniaJudgement?: SleepJudgementComparison;
}

/**
 * 判定のまとめ
 *
 * @param props {@link JudgementSectionProps}
 * @returns 判定のまとめ
 */
export default function JudgementSection({
  uuid,
  judgement,
  quantityJudgement,
  qualityJudgement,
  rhythmJudgement,
  impactScores,
  SASJudgement,
  insomniaJudgement,
}: JudgementSectionProps) {
  return (
    <SectionLayout title="判定のまとめ">
      <Typography
        variant="subtitle2"
        sx={{ fontSize: "16px", marginBottom: "8px" }}
      >
        総合判定と改善アクション
      </Typography>
      <TableContainer
        sx={{
          border: "solid #E0E0E0",
          borderWidth: "1px 1px 0 1px",
        }}
      >
        <Table>
          <TableHead
            sx={{
              "& .MuiTableCell-root": {
                backgroundColor: "primary.dark",
                color: "white",
              },
            }}
          >
            <TableRow>
              <TableCell sx={{ width: "127px" }}>
                <Stack direction="row">
                  <Typography variant="subtitle2">総合判定</Typography>
                  <ComprehensiveEvaluationHelpDialog
                    iconSx={{
                      color: "#FFFFFFB2",
                      alignSelf: "center",
                    }}
                  />
                </Stack>
              </TableCell>
              <TableCell
                sx={{
                  width: "50%",
                  paddingLeft: "0px",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    borderLeft: "2px solid #FFFFFF1F",
                    paddingLeft: "16px",
                  }}
                >
                  改善アクション
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell
                sx={{
                  borderRight: "1px solid #E0E0E0",
                  textAlign: "center",
                }}
              >
                <ComprehensiveEvaluation
                  evaluation={judgement.comprehensive_evaluation}
                />
              </TableCell>
              <TableCell>
                <ImprovementAction
                  uuid={uuid}
                  gradeAlphabet={
                    judgement.comprehensive_evaluation.grade_alphabet
                  }
                  improvementAction={judgement.improvement_action}
                  impactScores={impactScores}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={4}>
        {SASJudgement && insomniaJudgement && (
          <DiseaseRiskJudgementTable
            SASJudgement={SASJudgement}
            insomniaJudgement={insomniaJudgement}
          />
        )}
        <SleepJudgementTable
          quantityJudgement={quantityJudgement}
          qualityJudgement={qualityJudgement}
          rhythmJudgement={rhythmJudgement}
        />
      </Stack>
      {judgement.comprehensive_evaluation.is_evaluation_changed && (
        <Stack
          direction="row"
          sx={{
            marginTop: "36px",
            backgroundColor: "rgba(2, 136, 209, 0.2)",
            padding: "20px",
            borderRadius: "8px",
            justifyContent: "space-between",
          }}
        >
          <Stack>
            <Typography
              sx={{
                fontWeight: "500",
                color: "primary.dark",
              }}
            >
              判定の仕組みが変更されました。
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "primary.dark",
              }}
            >
              同じ値でも前回測定時と判定が異なる場合があります。詳細はマニュアルをご確認ください。
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
              fontSize: "13px",
              color: "primary.main",
            }}
          >
            <Link
              href="https://drive.google.com/drive/folders/1BZlazIgLrKH5qm54DINYRzt1VgeWY9w1"
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{ fontSize: "13px" }}
            >
              詳細を見る
            </Link>
            <NavigateNextIcon />
          </Stack>
        </Stack>
      )}
    </SectionLayout>
  );
}

function getGradeText(gradeAlphabet: string) {
  const alphabet = gradeAlphabet.charAt(0);
  const asterisk = gradeAlphabet.charAt(1);
  return `${alphabet !== "-" ? alphabet : "判定不能"}${asterisk}`;
}

interface ComprehensiveEvaluationProps {
  evaluation: SleepSummary2["judgement"]["comprehensive_evaluation"];
}
function ComprehensiveEvaluation({ evaluation }: ComprehensiveEvaluationProps) {
  const lastTimeExists =
    evaluation?.last_time_grade_alphabet != null &&
    evaluation?.last_time_grade_comment != null;
  const grade = getGradeText(evaluation.grade_alphabet);

  return (
    <>
      <Stack
        direction="row"
        sx={{
          fontSize: "34px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography
          component="span"
          sx={{ fontSize: "inherit", alignSelf: "end" }}
        >
          {grade}
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: "inherit", paddingLeft: "16px" }}
        >
          |
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: "inherit", paddingLeft: "8px" }}
        >
          {evaluation.grade_comment}
          {evaluation.is_under_treatment && (
            <Typography component="sup">※</Typography>
          )}
        </Typography>
      </Stack>
      {lastTimeExists && (
        <Stack
          direction="row"
          sx={{ justifyContent: "center", marginTop: "16px" }}
        >
          <Stack direction="row" sx={{ alignItems: "center" }}>
            <MoveImprovement improvement={evaluation.improvement} />
            <Typography sx={{ marginLeft: "4px", color: "text.secondary" }}>
              前回:
              {evaluation.last_time_grade_alphabet &&
                getGradeText(evaluation.last_time_grade_alphabet)}{" "}
              | {evaluation.last_time_grade_comment}
            </Typography>
          </Stack>
          {evaluation.last_time_is_under_treatment && (
            <Typography
              component="sup"
              sx={{ fontSize: "70%", lineHeight: "20px" }}
            >
              ※
            </Typography>
          )}
        </Stack>
      )}
    </>
  );
}

interface ImprovementActionProps {
  uuid: string;
  gradeAlphabet: string;
  improvementAction: SleepSummary2["judgement"]["improvement_action"];
  impactScores: ImpactScore[];
}
function ImprovementAction({
  uuid,
  gradeAlphabet,
  improvementAction,
  impactScores,
}: ImprovementActionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const alphabet = gradeAlphabet.charAt(0);
  const subTitles =
    improvementAction?.title && improvementAction.title.length > 1
      ? improvementAction.title.slice(0, -1)
      : null;
  const title = improvementAction?.title
    ? improvementAction.title[improvementAction.title.length - 1]
    : null;

  return (
    <>
      {subTitles &&
        subTitles.map((subTitle, idx) => (
          <Typography sx={{ fontSize: "14px", fontWeight: "500" }} key={idx}>
            {subTitle}
          </Typography>
        ))}
      {title && (
        <Typography sx={{ fontSize: "20px", fontWeight: "500" }}>
          {title}
        </Typography>
      )}
      {improvementAction?.comment ? (
        improvementAction.comment.map((comment, idx) => (
          <Typography
            key={idx}
            sx={{
              fontSize: "14px",
              color: "text.secondary",
              marginTop: "16px",
            }}
          >
            {comment}
          </Typography>
        ))
      ) : (
        <Typography
          sx={{
            fontSize: "14px",
            color: "text.secondary",
          }}
        >
          -
        </Typography>
      )}
      {improvementAction.action && (
        <Link
          sx={{
            cursor: "pointer",
            float: "inline-end",
          }}
          underline="none"
          onClick={() => {
            if (["B", "C", "D"].includes(alphabet)) {
              setDialogOpen(true);
            } else if (alphabet === "E") {
              window.open(`/facility/report/${uuid}/disease_risk`, "_blank");
            }
          }}
        >
          <Stack
            direction="row"
            component="span"
            sx={{
              fontSize: "14px",
              fontWeight: "500",
              width: "fit-content",
              alignItems: "center",
            }}
          >
            {improvementAction.action}
            {alphabet === "E" && (
              <OpenInNewIcon sx={{ marginLeft: "8px", fontSize: "14px" }} />
            )}
          </Stack>
        </Link>
      )}
      <WorksheetDialog
        title="睡眠の質に悪影響を与えた生活習慣の記録"
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
      >
        <ImpactScoresTable impactScores={impactScores} />
      </WorksheetDialog>
    </>
  );
}

interface ItemComparison {
  item: string;
  comparison: SleepJudgementComparison;
}

interface ItemComparisonTableProps {
  itemComparisons: ItemComparison[];
}

function ItemComparisonTable({ itemComparisons }: ItemComparisonTableProps) {
  return (
    <TableContainer
      sx={{
        border: "solid #E0E0E0",
        borderWidth: "1px 1px 0 1px",
      }}
    >
      <Table>
        <TableHead
          sx={{
            "& .MuiTableCell-root": {
              backgroundColor: "primary.dark",
              color: "white",
            },
          }}
        >
          <TableRow>
            <TableCell width="50%">
              <Typography variant="subtitle2">項目</Typography>
            </TableCell>
            <TableCell
              sx={{
                paddingLeft: "0px",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  borderLeft: "2px solid #FFFFFF1F",
                  paddingLeft: "16px",
                }}
              >
                判定
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {itemComparisons.map((itemComparison, idx) => (
            <TableRow key={idx}>
              <TableCell
                sx={{
                  borderRight: "1px solid #E0E0E0",
                }}
              >
                <Typography variant="subtitle2">
                  {itemComparison.item}
                </Typography>
              </TableCell>
              <TableCell>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ alignItems: "center" }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "16px", fontWeight: "500" }}
                  >
                    {itemComparison.comparison.result}
                  </Typography>
                  {itemComparison.comparison.improvement && (
                    <MoveImprovement
                      improvement={itemComparison.comparison.improvement}
                    />
                  )}
                  {itemComparison.comparison.last_time_result && (
                    <Typography
                      variant="body1"
                      sx={{ fontSize: "12px", color: "text.secondary" }}
                    >
                      前回:{itemComparison.comparison.last_time_result}
                    </Typography>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface SleepJudgementTableProps {
  quantityJudgement: SleepJudgementComparison;
  qualityJudgement: SleepJudgementComparison;
  rhythmJudgement: SleepJudgementComparison;
}
function SleepJudgementTable({
  quantityJudgement,
  qualityJudgement,
  rhythmJudgement,
}: SleepJudgementTableProps) {
  const sleepJudgement: ItemComparison[] = [
    {
      item: "睡眠の長さ",
      comparison: quantityJudgement,
    },
    {
      item: "睡眠の質",
      comparison: qualityJudgement,
    },
    {
      item: "睡眠のリズム",
      comparison: rhythmJudgement,
    },
  ];
  return (
    <Stack sx={{ width: "100%" }}>
      <Typography
        variant="subtitle2"
        sx={{ fontSize: "16px", marginBottom: "8px", marginTop: "36px" }}
      >
        睡眠の判定
      </Typography>
      <ItemComparisonTable itemComparisons={sleepJudgement} />
    </Stack>
  );
}
interface DiseaseRiskJudgementTableProps {
  SASJudgement: SleepJudgementComparison;
  insomniaJudgement: SleepJudgementComparison;
}
function DiseaseRiskJudgementTable({
  SASJudgement,
  insomniaJudgement,
}: DiseaseRiskJudgementTableProps) {
  const diseaseRiskJudgement: ItemComparison[] = [
    {
      item: "睡眠時無呼吸症候群のリスク",
      comparison: SASJudgement,
    },
    {
      item: "不眠症のリスク",
      comparison: insomniaJudgement,
    },
  ];
  return (
    <Stack sx={{ width: "100%" }}>
      <Typography
        variant="subtitle2"
        sx={{ fontSize: "16px", marginBottom: "8px", marginTop: "36px" }}
      >
        疾患リスクの判定
      </Typography>
      <ItemComparisonTable itemComparisons={diseaseRiskJudgement} />
    </Stack>
  );
}

interface ImpactScoresTableProps {
  impactScores: ImpactScore[];
}
function ImpactScoresTable({ impactScores }: ImpactScoresTableProps) {
  if (impactScores.length === 0) {
    return <></>;
  }

  const dayCount = impactScores[0].each_day_result.length;
  const worstScore = impactScores[0].value;
  const WORST_SCORE_THRESHOLD = 55;
  return (
    <>
      {(worstScore == null || worstScore < WORST_SCORE_THRESHOLD) && (
        <Typography
          sx={{
            padding: "16px",
            backgroundColor: "#FF3A3014",
            color: "error.main",
          }}
        >
          今回の測定で睡眠の質に悪影響を強く与えていそうな生活習慣はありませんでした。
        </Typography>
      )}
      <Stack
        direction="row"
        spacing={2}
        sx={{ marginTop: "16px", marginBottom: "8px", alignSelf: "end" }}
      >
        <ImpactScoreResultImg type={ThreeLevelScale.BAD} />
        <Typography>生活習慣に課題があった日</Typography>
      </Stack>
      <TableContainer
        sx={{
          borderRadius: "0px",
          border: "solid #E0E0E0",
          borderWidth: "1px 1px 0 1px",
        }}
      >
        <Table>
          <TableHead
            sx={{
              "& .MuiTableCell-root": {
                backgroundColor: "primary.dark",
                color: "white",
              },
            }}
          >
            <TableRow sx={{}}>
              <TableCell sx={{ width: "96px", paddingX: "0px" }}>
                <Typography sx={{ marginLeft: "16px", fontSize: "12px" }}>
                  影響スコア
                </Typography>
              </TableCell>
              <TableCell sx={{ width: "184px", paddingX: "0px" }}>
                <Typography
                  sx={{
                    borderLeft: "2px solid #FFFFFF1F",
                    paddingLeft: "16px",
                    fontSize: "12px",
                  }}
                >
                  項目
                </Typography>
              </TableCell>
              {Array.from({ length: dayCount }).map((_, i) => (
                <TableCell key={i} sx={{ paddingLeft: "0px" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      borderLeft: "2px solid #FFFFFF1F",
                      paddingLeft: "16px",
                      fontSize: "12px",
                    }}
                  >
                    {i + 1}日目
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {impactScores.map((impactScore, idx) => (
              <TableRow key={idx} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <TableCell
                  sx={{ textAlign: "center", borderRight: "1px solid #E0E0E0" }}
                >
                  {impactScore.value || "-"}
                </TableCell>
                <TableCell sx={{ borderRight: "1px solid #E0E0E0" }}>
                  {impactScore.caption}
                </TableCell>
                {impactScore.each_day_result.map((d, i) => (
                  <TableCell key={i} sx={{ textAlign: "center" }}>
                    <ImpactScoreResultImg type={d} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: "400",
          color: "text.secondary",
          marginTop: "8px",
        }}
      >
        ※影響スコア：睡眠の質に悪影響を与えた可能性を評価し、スコアが55以上で悪影響あり、100に近いほど悪影響が強いことを示します。
      </Typography>
    </>
  );
}
